import { useEffect, useMemo, useState } from 'react';
import {
  AudienceCategoryItem,
  AudiencePlatformItem,
  AudienceResponseMeta,
  AudienceSetItem,
  CountryItem,
  TaxonomyApi,
} from '@digiseg-labs/api-client';

type UseCategoryParams = {
  categoryCode?: string;
  platform?: AudiencePlatformItem | string;
  country?: CountryItem | string;
  enabled?: boolean;
};

const findCategoryInAudienceList = (
  data: AudienceCategoryItem[] | AudienceSetItem[] | undefined,
  categoryCode: string,
) => {
  if (!data?.length) return undefined;

  const firstItem = data[0] as Partial<AudienceSetItem>;
  const categories = Array.isArray(firstItem.categories)
    ? (data as AudienceSetItem[]).flatMap((setItem) => setItem.categories ?? [])
    : (data as AudienceCategoryItem[]);

  return categories.find((category) => category.code === categoryCode);
};

export const useCategory = ({
  categoryCode,
  platform,
  country,
  enabled = true,
}: UseCategoryParams = {}) => {
  const [category, setCategory] = useState<AudienceCategoryItem | undefined>(undefined);
  const [meta, setMeta] = useState<AudienceResponseMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const platformCode = useMemo(() => (
    typeof platform === 'string' ? platform : platform?.code
  ), [platform]);

  const countryCode = useMemo(() => (
    typeof country === 'string' ? country : country?.code
  ), [country]);

  useEffect(() => {
    if (!enabled || !categoryCode) {
      setCategory(undefined);
      setMeta(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const api = new TaxonomyApi();

    setCategory(undefined);
    setMeta(null);
    setIsLoading(true);
    setError(null);

    console.log('Loading category with', { categoryCode, platformCode, countryCode });
    api.getCategory(categoryCode, platformCode || undefined, countryCode || undefined)
      .then(async (resp) => {
        console.log('fff', resp.data);
        const directCategory = resp.data.data;
        const directMeta = resp.data.meta ?? null;

        if (directCategory?.audiences?.length) {
          return { category: directCategory, meta: directMeta };
        }

        const audienceListResp = await api.listAudiences(platformCode || '', countryCode || '');
        const fallbackCategory = findCategoryInAudienceList(
          audienceListResp.data.data as AudienceCategoryItem[] | AudienceSetItem[] | undefined,
          categoryCode,
        );
        return {
          category: fallbackCategory ?? directCategory,
          meta: directMeta,
        };
      })
      .then(({ category: resolvedCategory, meta: resolvedMeta }) => {
        if (cancelled) return;
        setCategory(resolvedCategory);
        setMeta(resolvedMeta);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        console.error('Failed to load category', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryCode, platformCode, countryCode, enabled]);

  return { category, meta, isLoading, error };
};
